import React from "react";
import styled from "styled-components";
import { useNavigate, useRouteError, isRouteErrorResponse } from "react-router-dom";
import { Search } from "lucide-react";
import {
  typographyTokens,
  spacingTokens,
  borderRadiusTokens,
} from "../../config/designTokens";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: ${spacingTokens.xl};
  background-color: ${(props) => props.theme.background.default};
  text-align: center;
`;

const ErrorCode = styled.h1`
  font-family: ${typographyTokens.fontFamily.bold};
  font-size: 120px;
  font-weight: ${typographyTokens.fontWeight.bold};
  color: ${(props) => props.theme.text.primary};
  margin: 0;
  line-height: 1;
`;

const Title = styled.h2`
  font-family: ${typographyTokens.fontFamily.semiBold};
  font-size: ${typographyTokens.fontSize.heading};
  font-weight: ${typographyTokens.fontWeight.semibold};
  color: ${(props) => props.theme.text.primary};
  margin: ${spacingTokens.md} 0 ${spacingTokens.sm} 0;
`;

const Description = styled.p`
  font-family: ${typographyTokens.fontFamily.primary};
  font-size: ${typographyTokens.fontSize.body};
  color: ${(props) => props.theme.text.secondary};
  margin: 0 0 ${spacingTokens.xl} 0;
  max-width: 500px;
`;

const ErrorDetails = styled.pre`
  background-color: ${(props) => props.theme.background.subtle};
  padding: ${spacingTokens.md};
  border-radius: ${borderRadiusTokens.sm};
  color: ${(props) => props.theme.text.primary};
  font-family: monospace;
  font-size: ${typographyTokens.fontSize.body};
  margin-bottom: ${spacingTokens.lg};
  max-width: 800px;
  overflow-x: auto;
  text-align: left;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${spacingTokens.md};
  flex-wrap: wrap;
  justify-content: center;
`;

const Button = styled.button<{ $primary?: boolean }>`
  font-family: ${typographyTokens.fontFamily.medium};
  font-size: ${typographyTokens.fontSize.body};
  font-weight: ${typographyTokens.fontWeight.medium};
  padding: ${spacingTokens.md} ${spacingTokens.xl};
  border: 1px solid ${(props) => props.theme.text.primary};
  border-radius: ${borderRadiusTokens.md};
  background-color: ${(props) =>
    props.$primary ? props.theme.text.primary : "transparent"};
  color: ${(props) =>
    props.$primary
      ? props.theme.text.inverse
      : props.theme.text.primary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${(props) =>
    props.$primary ? props.theme.neutral.grey[800] : props.theme.neutral.grey[100]};
  }
`;

const Illustration = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${spacingTokens.md};
  color: ${(props) => props.theme.text.primary};
`;

export const NotFoundPage: React.FC = () => {
  const navigate = useNavigate();
  const error = useRouteError();
  console.error("Route Error:", error);

  let errorTitle = "404";
  let errorSubtitle = "Page Not Found";
  let errorDescription = "The page you're looking for doesn't exist or has been moved.";
  let errorDetails = "";

  if (isRouteErrorResponse(error)) {
    errorTitle = error.status.toString();
    errorSubtitle = error.statusText || "Error";
    if (error.status === 404) {
      errorSubtitle = "Page Not Found";
      errorDescription = "The page you're looking for doesn't exist or has been moved.";
    } else {
      errorDescription = error.data?.message || "An unexpected error occurred.";
    }
  } else if (error instanceof Error) {
    // Handle ChunkLoadError by auto-reloading (once) to get fresh chunks
    const isChunkError =
      error.name === 'ChunkLoadError' ||
      error.message.includes('Loading chunk') ||
      error.message.includes('Loading CSS chunk') ||
      error.message.includes('Failed to fetch dynamically imported module');

    if (isChunkError) {
      const hasReloaded = sessionStorage.getItem('chunk_error_reloaded');
      if (!hasReloaded) {
        sessionStorage.setItem('chunk_error_reloaded', 'true');
        window.location.reload();
        return null; // Don't render while reloading
      }
      // If we've already reloaded once, clear the flag and show the error
      sessionStorage.removeItem('chunk_error_reloaded');

      errorTitle = "App Updated";
      errorSubtitle = "Version mismatch";
      errorDescription = "A new version of the app is available. Please refresh the page to load the latest version.";
    } else {
      errorTitle = "Oops!";
      errorSubtitle = "Something went wrong";
      errorDescription = "An unexpected application error occurred.";
      errorDetails = error.stack || error.message;
    }
  }

  return (
    <Container data-testid="not-found-page">
      <Illustration><Search size={64} /></Illustration>
      <ErrorCode>{errorTitle}</ErrorCode>
      <Title>{errorSubtitle}</Title>
      <Description>
        {errorDescription}
      </Description>

      {errorDetails && (
        <ErrorDetails>
          {errorDetails}
        </ErrorDetails>
      )}

      <ButtonGroup>
        <Button onClick={() => navigate(-1)}>Go Back</Button>
        <Button $primary onClick={() => navigate("/")}>
          Go to Dashboard
        </Button>
      </ButtonGroup>
    </Container>
  );
};

export default NotFoundPage;
